import type { AxiosInstance, AxiosRequestConfig } from "axios";
import axios from "axios";
import type { ApiErrorResponse, ApiResponse } from "@core/types/TApiResponse";

export class BlobApi {
  private axiosInstance: AxiosInstance;

  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  private handleError(error: unknown): ApiErrorResponse {
    if (axios.isAxiosError(error)) {
      const data = error.response?.data as ApiResponse<unknown> | undefined;

      return {
        success: false,
        message: data?.message ?? error.message ?? "Something went wrong",
        error: {
          code: data?.error?.code ?? error.code ?? "UNKNOWN_ERROR",
          message: data?.error?.message ?? error.message ?? "Unexpected error",
          details: {
            ...(data?.error?.details ?? {}),
            status: error.response?.status,
          },
        },
        data: null,
      };
    }

    return {
      success: false,
      message: "Unexpected error",
      error: {
        code: "UNEXPECTED_ERROR",
        message: "Unexpected error occurred",
        details: {},
      },
      data: null,
    };
  }

  async get(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    try {
      const response = await this.axiosInstance.get(url, {
        ...config,
        responseType: "blob",
        headers: {
          Accept: "*/*",
          ...(config?.headers ?? {}),
        },
      });

      return response.data as Blob;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async post<D = unknown>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig,
  ): Promise<Blob> {
    try {
      const response = await this.axiosInstance.post(url, data, {
        ...config,
        responseType: "blob",
        headers: {
          Accept: "*/*",
          ...(config?.headers ?? {}),
        },
      });

      return response.data as Blob;
    } catch (error) {
      throw this.handleError(error);
    }
  }
}
